import { BreakdownDisplayMode } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/breakdown-display-mode";
import { Currency } from "@redotech/money/currencies";
import { CorrelatedCarrierServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  DynamicRateRounding,
  RateTableType,
} from "./shipping-rate-table-enums";

/**
 * Shopify's carrier service API sends us the shop's base currency, not the
 * customer's browsing currency. We guess the customer's currency from their
 * destination country and pre-convert the landed cost breakdown into that
 * currency. When the guess is correct, the checkout UI can use these values
 * directly — avoiding a second conversion that would introduce rounding
 * mismatches between the breakdown and the total price Shopify displays.
 */
const landedCostAmountsSchema_ = z
  .object({
    duties: z.number().meta({ privacy: privacy.nonPii }),
    taxes: z.number().meta({ privacy: privacy.nonPii }),
    fees: z.number().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

const customerCurrencyEstimateSchema_ = z
  .object({
    duties: z.number().meta({ privacy: privacy.nonPii }), // minor units in guessed customer currency
    taxes: z.number().meta({ privacy: privacy.nonPii }), // minor units in guessed customer currency
    fees: z.number().meta({ privacy: privacy.nonPii }), // minor units in guessed customer currency
    currency: createEnum(Currency, { enumName: "Currency" }).meta({
      privacy: privacy.nonPii,
    }),
    exchangeRate: z.number().meta({ privacy: privacy.nonPii }), // shop currency → guessed customer currency
    original: landedCostAmountsSchema_
      .optional()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const customerCurrencyEstimateSchema: z.ZodType<CustomerCurrencyEstimate> =
  customerCurrencyEstimateSchema_;
export interface CustomerCurrencyEstimate extends z.infer<
  typeof customerCurrencyEstimateSchema_
> {}

const landedCostDataSchema_ = z
  .object({
    zonosQuoteId: z.string().meta({ privacy: privacy.nonPii }),
    duties: z.number().meta({ privacy: privacy.nonPii }), // minor units, shop currency
    taxes: z.number().meta({ privacy: privacy.nonPii }), // minor units, shop currency
    fees: z.number().meta({ privacy: privacy.nonPii }), // minor units, shop currency
    currency: createEnum(Currency, { enumName: "Currency" }).meta({
      privacy: privacy.nonPii,
    }),
    /** Present only when the customer's guessed currency differs from the shop's. */
    customerCurrencyEstimate: customerCurrencyEstimateSchema
      .optional()
      .meta({ privacy: privacy.structured }),
    original: landedCostAmountsSchema_
      .optional()
      .meta({ privacy: privacy.structured }),
    displayMode: createEnum(BreakdownDisplayMode, {
      enumName: "BreakdownDisplayMode",
    })
      .optional()
      .meta({ privacy: privacy.nonPii }),
    breakdownLabelOverrides: z
      .object({
        shipping: z.string().optional().meta({ privacy: privacy.nonPii }),
        taxes: z.string().optional().meta({ privacy: privacy.nonPii }),
        dutiesAndFees: z.string().optional().meta({ privacy: privacy.nonPii }),
      })
      .optional()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const landedCostDataSchema: z.ZodType<LandedCostData> =
  landedCostDataSchema_;
export interface LandedCostData extends z.infer<typeof landedCostDataSchema_> {}

const baseRedoShippingOptionSchema = z
  .object({
    provider: z.literal("redo").meta({ privacy: privacy.nonPii }),
    rateTableId: zExt.objectId().meta({ privacy: privacy.nonPii }),
    serviceCodeSentToCheckoutWhenMetafieldsUsed: z
      .string()
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    checkoutTreeNodeId: z.string().meta({ privacy: privacy.nonPii }),
    checkoutExperienceId: z
      .string()
      .optional()
      .meta({ privacy: privacy.nonPii }),
    assignedTreatmentId: z.string().nullish().meta({ privacy: privacy.nonPii }),
    coverageTypes: z
      .object({
        return: z.boolean().optional().meta({ privacy: privacy.nonPii }),
        packageProtection: z
          .boolean()
          .optional()
          .meta({ privacy: privacy.nonPii }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    landedCostData: landedCostDataSchema
      .optional()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

const markupSchema = z
  .object({
    markupPercentage: z.string().optional().meta({ privacy: privacy.nonPii }),
    markupAmount: z.string().optional().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

const costBreakdownSchema = z
  .object({
    costCurrency: createEnum(Currency, { enumName: "Currency" }).meta({
      privacy: privacy.nonPii,
    }),
    labelCost: z.string().meta({ privacy: privacy.nonPii }),
    markup: markupSchema.optional().meta({ privacy: privacy.structured }),
    rounding: createEnum(DynamicRateRounding, {
      enumName: "DynamicRateRounding",
    })
      .optional()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export interface CostBreakdown extends z.infer<typeof costBreakdownSchema> {}

const dynamicRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.DYNAMIC).meta({ privacy: privacy.nonPii }),
  carrierService: CorrelatedCarrierServiceSchema,
  /**
   * Multiple parcels can cause a rate table to have multiple carrier services
   */
  carrierServices: z.array(CorrelatedCarrierServiceSchema).optional(),
  costBreakdown: costBreakdownSchema,
  promisedDeliveryDate: z.string().optional().meta({ privacy: privacy.nonPii }),
});

const fixedRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.FIXED).meta({ privacy: privacy.nonPii }),
});

const customRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.CUSTOM).meta({ privacy: privacy.nonPii }),
});

export const checkoutShippingOptionSchema_ = z
  .union([
    dynamicRedoShippingOptionSchema.meta({ privacy: privacy.structured }),
    fixedRedoShippingOptionSchema.meta({ privacy: privacy.structured }),
    customRedoShippingOptionSchema.meta({ privacy: privacy.structured }),
  ])
  .meta({ privacy: privacy.structured });
export const checkoutShippingOptionSchema: z.ZodType<CheckoutShippingOption> =
  checkoutShippingOptionSchema_;
export type CheckoutShippingOption = z.infer<
  typeof checkoutShippingOptionSchema_
>;
